import React, { useEffect, useState } from "react";
import { Landmark, ShieldCheck, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { payFranchiseOrder } from "@/lib/franchisePay";
import { PageHeader, Badge, statusTone, Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BANK_STATUS = { fee_pending: "Verification fee unpaid", pending_review: "Pending verification", verified: "Verified", rejected: "Rejected" };

function BankForm({ onDone, fee }) {
  const [f, setF] = useState({ holder_name: "", account_number: "", ifsc: "", bank_name: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!/^\d{9,18}$/.test(f.account_number) || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(f.ifsc.toUpperCase()) || f.holder_name.trim().length < 3) return toast.error("Check the account holder name, account number and IFSC");
    setBusy(true);
    try {
      const { data } = await api.post("/franchise/institute/bank", f);
      const st = await payFranchiseOrder(data, { description: "Bank account verification fee" });
      if (st === "paid") toast.success("Fee paid. Your bank account is pending verification.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="bank-form">
      <div><Label>Account holder name *</Label><Input value={f.holder_name} onChange={(e) => set("holder_name", e.target.value)} data-testid="bank-holder" /></div>
      <div><Label>Bank name</Label><Input value={f.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="bank-name" /></div>
      <div><Label>Account number *</Label><Input inputMode="numeric" value={f.account_number} onChange={(e) => set("account_number", e.target.value.replace(/\D/g, ""))} data-testid="bank-account" /></div>
      <div><Label>IFSC *</Label><Input value={f.ifsc} onChange={(e) => set("ifsc", e.target.value.toUpperCase())} maxLength={11} data-testid="bank-ifsc" /></div>
      <div className="sm:col-span-2"><Button disabled={busy} onClick={submit} className="viriks-amber text-white" data-testid="bank-submit">{busy ? "Please wait..." : `Save & pay ₹${fee} verification fee`}</Button>
        <p className="text-xs text-muted-foreground mt-2">The ₹{fee} fee covers verification processing. Paying it does not by itself verify the account — the VIRIKS team confirms ownership before withdrawals are enabled.</p></div>
    </div>
  );
}

function Withdraw({ balance, min, onDone }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post("/franchise/institute/withdrawals", { amount: Number(amount) }); toast.success("Withdrawal requested"); setAmount(""); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div><Label>Amount (₹)</Label><Input type="number" min={min} max={balance} value={amount} onChange={(e) => setAmount(e.target.value)} className="w-48" data-testid="wd-amount" /></div>
      <Button disabled={busy || !amount} onClick={go} className="viriks-navy text-white" data-testid="wd-submit">Request withdrawal</Button>
      <p className="text-xs text-muted-foreground w-full">Available: <Money amount={balance} /> • Minimum ₹{min}</p>
    </div>
  );
}

export default function InstituteBank() {
  const [bank, setBank] = useState(undefined);
  const [ledger, setLedger] = useState(null);
  const [cfg, setCfg] = useState({ bank_fee: 9, min_withdrawal: 500 });
  const [editing, setEditing] = useState(false);
  const load = () => {
    api.get("/franchise/institute/bank").then(({ data }) => setBank(data));
    api.get("/franchise/institute/ledger").then(({ data }) => setLedger(data));
  };
  useEffect(() => { load(); api.get("/franchise/config").then(({ data }) => setCfg(data)); }, []);
  const payFee = async () => {
    try { const { data } = await api.post(`/franchise/institute/bank/${bank.id}/pay`); await payFranchiseOrder(data, { description: "Bank account verification fee" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (bank === undefined || !ledger) return null;
  const showForm = !bank || editing || bank.status === "rejected";
  return (
    <div data-testid="institute-bank">
      <PageHeader title="Bank & Withdrawals" subtitle="Verified bank account required for commission payouts" />
      <div className="bg-white border border-border rounded-lg p-6 mb-6">
        <h3 className="font-heading font-bold text-navy mb-4 flex items-center gap-2"><Landmark size={18} /> Bank account</h3>
        {bank && !editing && (
          <div className="flex flex-wrap items-center gap-4 mb-4" data-testid="bank-current">
            <div><div className="font-semibold">{bank.holder_name}</div><div className="text-sm text-muted-foreground font-mono">{bank.account_number} • {bank.ifsc}{bank.bank_name ? ` • ${bank.bank_name}` : ""}</div></div>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full ${bank.status === "verified" ? "bg-emerald-100 text-emerald-700" : bank.status === "rejected" ? "bg-crimson/10 text-crimson" : "bg-yellow-100 text-yellow-800"}`} data-testid="bank-status">
              {bank.status === "verified" ? <ShieldCheck size={13} /> : <Clock size={13} />} {BANK_STATUS[bank.status]}
            </span>
            {bank.status === "fee_pending" && <Button size="sm" className="viriks-amber text-white" onClick={payFee} data-testid="bank-pay-fee">Pay ₹{cfg.bank_fee} fee</Button>}
            {bank.status === "verified" && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="bank-change">Change account</Button>}
            {bank.remark && <p className="text-sm text-muted-foreground w-full">Note: {bank.remark}</p>}
          </div>
        )}
        {showForm && bank?.status !== "pending_review" && <BankForm fee={cfg.bank_fee} onDone={() => { setEditing(false); load(); }} />}
      </div>
      <div className="bg-white border border-border rounded-lg p-6 mb-6">
        <h3 className="font-heading font-bold text-navy mb-4">Request a withdrawal</h3>
        {bank?.status === "verified" ? <Withdraw balance={ledger.available_balance} min={cfg.min_withdrawal} onDone={load} /> : <p className="text-sm text-muted-foreground" data-testid="wd-locked">Withdrawals unlock once your bank account is verified.</p>}
      </div>
      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground"><tr>{["Requested", "Amount", "To", "Status", "UTR / Note"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {ledger.withdrawals.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No withdrawals yet.</td></tr>}
            {ledger.withdrawals.map((w) => (
              <tr key={w.id} className="border-t border-border" data-testid={`wd-row-${w.id}`}>
                <td className="px-4 py-3">{new Date(w.created_at).toLocaleDateString("en-IN")}</td>
                <td className="px-4 py-3 font-semibold"><Money amount={w.amount} /></td>
                <td className="px-4 py-3 font-mono">{w.bank_masked}</td>
                <td className="px-4 py-3"><Badge tone={statusTone(w.status)}>{w.status}</Badge></td>
                <td className="px-4 py-3 text-xs">{w.utr || w.failure_reason || w.remark || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
